import React from 'react';
import PropTypes from 'prop-types';
import { SquarePen, Plus } from 'lucide-react';
import Button from '../../Button';
import Price from '../../Price';
import CardZoom from '../shared/CardZoom';
import '../../shared/Card.css';
import './TextCard.css';

/**
 * TextCard — Configurator
 *
 * Horizontal option card: 80px thumbnail, uppercase title, optional
 * description and price, and one corner action — Edit (SquarePen,
 * Tertiary/Text) for a configured option, Add (Plus, Tertiary/Gray) for one
 * that is not yet.
 *
 * Figma component set: 4287:23974
 * Variants: State = Default (4287:23975) | Hover (4287:23985)
 *           Edit / Add / Description / Price — boolean toggles
 *
 * The Figma booleans map to data: the Edit button renders with `onEdit`, Add
 * with `onAdd` (both share one slot, so `onEdit` wins), description and price
 * when set. `originalPrice` turns the price into the DS discounted price
 * (green price + struck-through original, OVER/LL Paket cards 2069:68784).
 * Hover is CSS-only.
 */

/* An array description renders its parts separated by a gray bullet */
const renderDescription = (description) => {
  if (!Array.isArray(description)) return description;
  return description.map((part, index) => (
    <React.Fragment key={index}>
      {index > 0 && <span className="cfg-text-card__bullet" aria-hidden="true"> • </span>}
      {part}
    </React.Fragment>
  ));
};

const TextCard = ({
  image,
  imageAlt = '',
  title,
  description,
  price,
  originalPrice,
  onClick,
  onEdit,
  onAdd,
  onZoom,
  editLabel = 'Edit',
  addLabel = 'Add',
  zoomLabel = 'Zoom',
  className = '',
}) => {
  const hasAction = Boolean(onEdit || onAdd);

  const classes = [
    'ds-card',
    'cfg-text-card',
    onClick && 'ds-card--interactive',
    hasAction && 'cfg-text-card--has-action',
    className,
  ].filter(Boolean).join(' ');

  return (
    <div className={classes}>
      {onClick && (
        <button
          type="button"
          className="ds-card__hit"
          onClick={onClick}
          aria-label={title || imageAlt || undefined}
        />
      )}

      <div className="cfg-text-card__wrapper">
        {onEdit ? (
          <Button
            size="tertiary"
            variant="text"
            iconOnly
            iconLeft={<SquarePen size={20} />}
            className="cfg-text-card__action"
            onClick={onEdit}
            aria-label={editLabel}
          />
        ) : onAdd && (
          <Button
            size="tertiary"
            variant="gray"
            iconOnly
            iconLeft={<Plus size={20} />}
            className="cfg-text-card__action cfg-text-card__action--add"
            onClick={onAdd}
            aria-label={addLabel}
          />
        )}

        <div className="cfg-text-card__thumb">
          {image && <img className="cfg-text-card__img" src={image} alt={imageAlt} />}
          <CardZoom onZoom={onZoom} label={zoomLabel} className="cfg-text-card__zoom" />
        </div>

        <div className="cfg-text-card__content">
          <div className="cfg-text-card__copy">
            <div className="cfg-text-card__details">
              {title && <span className="cfg-text-card__title typo-h5">{title}</span>}
              {description && (
                <span className="cfg-text-card__description typo-body-sm">
                  {renderDescription(description)}
                </span>
              )}
            </div>
            {price && (
              <Price
                type="small"
                state={originalPrice ? 'discounted' : 'default'}
                price={price}
                originalPrice={originalPrice}
                className="cfg-text-card__price"
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

TextCard.propTypes = {
  /** Thumbnail image URL (object-contain on white) */
  image: PropTypes.string,
  /** Alt text for the thumbnail */
  imageAlt: PropTypes.string,
  /** Option title (typo-h5, uppercase) */
  title: PropTypes.string,
  /** Muted description — an array renders its parts separated by a bullet */
  description: PropTypes.oneOfType([PropTypes.node, PropTypes.arrayOf(PropTypes.node)]),
  /** Formatted price, e.g. "112 kr" — rendered through the DS Price */
  price: PropTypes.string,
  /** Formatted pre-discount price — shows the price as discounted (green + struck original) */
  originalPrice: PropTypes.string,
  /** Card click handler — makes the whole card interactive */
  onClick: PropTypes.func,
  /** Edit handler — shows the SquarePen button (wins over onAdd) */
  onEdit: PropTypes.func,
  /** Add handler — shows the Plus button when there is no onEdit */
  onAdd: PropTypes.func,
  /** Zoom click handler — turns the zoom icon into its own button */
  onZoom: PropTypes.func,
  /** Accessible name of the Edit button */
  editLabel: PropTypes.string,
  /** Accessible name of the Add button */
  addLabel: PropTypes.string,
  /** Accessible name of the zoom button */
  zoomLabel: PropTypes.string,
  /** Additional CSS class */
  className: PropTypes.string,
};

export default TextCard;
